import React, { useState, useMemo } from "react";

import { Box, useMediaQuery } from "@material-ui/core";

import MapLoading from "components/MapLoading";

import { withStyles } from "@material-ui/core/styles";

import { parseISO, add, formatISO } from "date-fns";

import { format as d3Format } from "d3-format";

import { color as d3Color } from "d3-color";

import { useTranslation } from "react-i18next";

import { getDateLocale } from "lib/date-locale";

import MobilityTimelineTooltip from "./MobilityTimelineTooltip";

import { formatDateTick } from "./formatters";

import MobilityTimelineLegend from "./MobilityTimelineLegend";

import { CASES_KEY } from "lib/config";

import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Label,
  Legend,
} from "recharts";

import { useTheme } from "@material-ui/core/styles";

function getNextDay(dayStr) {
  const currDay = parseISO(dayStr);
  const tomorrow = add(currDay, {
    days: 1,
  });
  return formatISO(tomorrow, { representation: "date" });
}

function shouldRender(prevProps, nextProps) {
  return nextProps.data == null;
}

const AxisLabel = ({
  axisType,
  xOffset = 0,
  yOffset = 0,
  viewBox: { x, y, width, height },
  stroke,
  text,
}) => {
  const isVert = axisType === "yAxis";
  const cx = isVert ? x : x + width / 2;
  const cy = isVert ? height / 2 + y : y + height + 10;
  const rot = isVert ? `270 ${cx} ${cy}` : 0;

  const splitText = text.split("\\n");

  return (
    <g transform={`translate(${xOffset}, ${yOffset})`}>
      {splitText.map((t, idx) => (
        <text
          key={t}
          id="label-axis"
          x={cx}
          y={cy + idx * 12}
          transform={`rotate(${rot})`}
          textAnchor="middle"
          stroke={stroke}
        >
          {t}
        </text>
      ))}
    </g>
  );
};

const Timeline = React.memo(
  ({
    showBars,
    lines,
    data: rawData,
    selectedDate,
    onSelectDate,
    baselineStart,
    baselineEnd,
    maxDate,
    minDate,
  }) => {
    const [disabledLines, setDisabledLines] = useState({});

    const data = useMemo(
      () =>
        rawData &&
        rawData.filter(({ date }) => {
          return (!maxDate || date <= maxDate) && (!minDate || date >= minDate);
        }),
      [rawData, minDate, maxDate]
    );


    const enabledLines = useMemo(
      () => (!lines ? lines : lines.filter(({ id }) => !disabledLines[id])),
      [lines, disabledLines]
    );

    function handleToggleLine(id) {
      setDisabledLines(() => ({ ...disabledLines, [id]: !disabledLines[id] }));
    }

    const { t, i18n } = useTranslation();
    const locale = getDateLocale(i18n.language);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"), {
      defaultMatches: true,
    });

    const formatNumberTick = d3Format(isMobile ? "~s" : ",.2r");

    const formatPercentageTick = d3Format(".0%");

    const margin = isMobile
      ? { top: 6, right: 0, left: 0, bottom: 10 }
      : {
          top: 14,
          right: 25,
          left: 25,
          bottom: 10,
        };

    // xs, extra-small: 0px
    // sm, small: 600px
    // md, medium: 960px
    // lg, large: 1280px
    // xl, extra-large: 1920px

    // Just fixing the format for recharts. There must be a better way.
    const typographyToUse = theme.typography.body2;
    const typography = isMobile
      ? typographyToUse[theme.breakpoints.down("xs")]
      : {
          fontFamily: typographyToUse.fontFamily,
          fontWeight: typographyToUse.fontWeight,
          fontSize: typographyToUse.fontSize,
          lineHeight: typographyToUse.lineHeight,
          letterSpacing: typographyToUse.letterSpacing,
          color: typographyToUse.color,
        };

    function handleClick(event) {
      if (event && onSelectDate) {
        onSelectDate(event["activeLabel"]);
      }
    }

    function renderTimelineWithData() {
      if (!data) {
        return <div />;
      }

      return (
        <ComposedChart data={data} margin={margin} onClick={handleClick}>
          {/* {!isMobile ? <CartesianGrid strokeDasharray="2 2" /> : null} */}
          <XAxis
            xAxisId="date-axis"
            dataKey="date"
            minTickGap={7}
            tick={{ ...typography }}
            type="category"
            tickFormatter={formatDateTick(locale)}
          ></XAxis>

          <YAxis
            yAxisId="mobility-axis"
            tick={{ ...typography }}
            // domain={[-100, +20]}
            // ticks={[-100, -80, -60, -40, -20, 0, 20]}
            tickFormatter={formatPercentageTick}
            label={
              <AxisLabel
                xOffset={isMobile ? 20 : 10}
                axisType="yAxis"
                style={{
                  ...typography,
                }}
                text={t("mobilityTimeline.axis.mobilityChange")}
              ></AxisLabel>
            }
          />

          <YAxis
            yAxisId="reported-cases-axis"
            orientation="right"
            tick={{ ...typography }}
            tickFormatter={formatNumberTick}
            s
            label={
              <AxisLabel
                xOffset={isMobile ? 40 : 60}
                axisType="yAxis"
                style={{
                  ...typography,
                }}
                text={t("mobilityTimeline.axis.totalCases")}
              ></AxisLabel>
            }
          />

          <Legend
            content={
              <MobilityTimelineLegend
                lines={lines}
                disabledLines={disabledLines}
                onToggleLine={handleToggleLine}
              />
            }
            verticalAlign="top"
          />

          <Tooltip
            content={
              <MobilityTimelineTooltip
                mobilityChangeFormatter={formatPercentageTick}
                dateFormatter={formatDateTick(locale)}
                baselineDate={baselineStart}
                lines={enabledLines}
              />
            }
          />

          {/* Barras */}
          {showBars &&
            enabledLines.map(({ name, id, color, values }) => {
              if (values && !values.includes(CASES_KEY)) {
                return null;
              }

              const dataKey = `content.${id}.${CASES_KEY}`;

              return (
                <Bar
                  key={dataKey}
                  dataKey={dataKey}
                  name={t("mobilityTimeline.legend.totalCases", {
                    entity: name,
                  })}
                  xAxisId="date-axis"
                  yAxisId="reported-cases-axis"
                  fill={d3Color(color).copy({ opacity: 0.4 }).formatRgb()}
                  stackId="cases"
                  animationDuration={500}
                />
              );
            })}

          {/* Lines */}
          {enabledLines.map(({ name, id, color, values }) => {
            if (values && !values.includes("mobilityChange")) {
              return null;
            }

            const dataKey = `content.${id}.mobilityChange`;

            return (
              <Line
                style={{ display: disabledLines[id] ? "none" : "block" }}
                key={dataKey}
                dot={false}
                dataKey={dataKey}
                animationDuration={500}
                xAxisId="date-axis"
                yAxisId="mobility-axis"
                name={t("mobilityTimeline.legend.mobilityChange", {
                  entity: name,
                })}
                type="monotone"
                stroke={color}
                strokeWidth={2}
              />
            );
          })}

          {/* Selected day */}
          <ReferenceArea
            xAxisId="date-axis"
            yAxisId="reported-cases-axis"
            x1={selectedDate}
            x2={
              data[data.length - 1]?.date === selectedDate
                ? null
                : getNextDay(selectedDate)
            }
            ifOverflow="extendDomain"
            isFront={true}
            label={
              <Label
                style={{
                  ...typography,
                }}
                value={t("mobilityTimeline.reference.selectedDate")}
                angle={-90}
                position="center"
              />
            }
          />

          {/* BASELINE */}
          <ReferenceArea
            xAxisId="date-axis"
            yAxisId="mobility-axis"
            x1={baselineStart}
            x2={
              data[data.length - 1]?.date === baselineEnd
                ? null
                : getNextDay(baselineEnd)
            }
            label={
              <Label
                style={{
                  ...typography,
                }}
                value={t("mobilityTimeline.reference.baselineDate")}
                angle={-90}
                position="center"
              />
            }
          />
        </ComposedChart>
      );
    }

    return (
      <ResponsiveContainer
        minHeight={100}
        minWidth={200}
        height="100%"
        width="100%"
      >
        {renderTimelineWithData()}
      </ResponsiveContainer>
    );
  },
  shouldRender
);

const TimelineLoading = withStyles({
  position: "absolute",
  pointerEvents: "none",
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  zIndex: 100,
})(MapLoading);

function MobilityTimeline({ data, ...props }) {
  return (
    <Box height="100%">
      {!data ? <TimelineLoading /> : undefined}
      <Timeline data={data} {...props} />
    </Box>
  );
}

export default React.memo(MobilityTimeline);

if (process.env.NODE_ENV === "development") {
  MobilityTimeline.whyDidYouRender = {
    logOnDifferentValues: false,
  };
}
